import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "wouter";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import ScrollReveal from "@/components/ScrollReveal";
import { pageSEO, organizationSchema, serviceSchema } from "@/lib/seoConfig";

const steps = [
  ["01", "Intake & scorecard", "We define the commercial problem, success measures, reporting line, territory, and the evidence a successful hire must bring."],
  ["02", "Market map", "We map the relevant US packaging talent market by sector, customer base, geography, and level of responsibility."],
  ["03", "Target list & approach plan", "You see the target-company strategy and understand how the search will reach the right passive candidates."],
  ["04", "Confidential outreach", "We approach proven packaging sales professionals directly and handle every conversation confidentially."],
  ["05", "Assessment & interviews", "We assess commercial results, packaging knowledge, customer credibility, motivation, and fit against the scorecard."],
  ["06", "Shortlist with written notes", "You receive a focused shortlist with clear written context on each candidate, not a stack of resumes."],
  ["07", "Client interviews & feedback", "We coordinate interviews, gather feedback quickly, and keep both sides informed throughout the decision."],
  ["08", "Offer, close & follow-up", "We support the offer and close, then stay close through onboarding and the early transition."],
];

const processSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How retained packaging sales search works",
  description: "The eight-step retained search process used by OC Recruitment for US packaging sales and commercial leadership roles.",
  step: steps.map(([name, title, text]) => ({ "@type": "HowToStep", name: `${name} ${title}`, text })),
};

export default function Process() {
  return (
    <div>
      <SEO title={pageSEO.process.title} description={pageSEO.process.description} keywords={pageSEO.process.keywords} canonical={pageSEO.process.canonical} structuredData={[organizationSchema, serviceSchema, processSchema]} />
      <section className="bg-midnight pt-32 pb-20">
        <div className="container">
          <Breadcrumbs items={[{ name: "Our Process" }]} />
          <div className="max-w-3xl mt-10">
            <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">Retained Search Process</p>
            <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">A disciplined search for the commercial leader your business actually needs.</h1>
            <p className="text-lg text-white/70 leading-relaxed">OC Recruitment runs a transparent, eight-step retained search for US packaging companies. We map the market, approach passive talent, assess against a clear scorecard, and keep you informed from brief to onboarding.</p>
          </div>
        </div>
      </section>
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-3xl mb-14">
            <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">What happens next</p>
            <h2 className="text-3xl sm:text-4xl text-midnight mb-5">Eight steps. Full visibility.</h2>
            <p className="text-slate-brand leading-relaxed">Retained search works best when the brief is precise, the market feedback is honest, and every decision is made with the commercial outcome in view.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {steps.map(([number, title, text], index) => (
              <ScrollReveal key={number} delay={index * 0.03}>
                <div className="h-full border border-border p-7 rounded-sm bg-pale/30">
                  <p className="text-steel text-sm font-semibold tracking-[0.2em] mb-5">{number}</p>
                  <h3 className="text-xl text-midnight mb-3">{title}</h3>
                  <p className="text-slate-brand leading-relaxed">{text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-pale">
        <div className="container grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">What you can expect</p>
            <h2 className="text-3xl sm:text-4xl text-midnight mb-6">A search that gives you useful market intelligence.</h2>
            <p className="text-slate-brand leading-relaxed">You will know which companies are being mapped, what the market is telling us, and why each shortlisted candidate fits the commercial challenge. We do not hide behind generic updates or send unqualified resumes.</p>
          </div>
          <div className="space-y-4">
            {["Regular progress updates", "Full visibility of who is approached", "Honest market feedback", "Confidential handling throughout"].map(item => <div key={item} className="flex items-center gap-3 text-midnight"><CheckCircle2 className="text-steel" size={20} /><span>{item}</span></div>)}
          </div>
        </div>
      </section>
      <section className="py-20 bg-midnight text-white text-center">
        <div className="container max-w-3xl">
          <h2 className="text-3xl sm:text-4xl mb-5">Have a search open?</h2>
          <p className="text-white/70 leading-relaxed mb-8">Tell Chris what the business needs to achieve and he will explain how a retained search can help.</p>
          <Link href="/contact" className="inline-flex items-center gap-3 px-6 py-3 bg-white text-midnight font-semibold rounded-sm hover:bg-white/90 transition-colors">Discuss Your Search <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
